import { useEffect, useRef, useState } from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { LearnerCertificateDto } from '../../general/dtos';
import { useApi } from '../../providers/api-provider';
import { GeneralInfoDialog } from './info-dialog';

export function CertificatePrintRequestDialog({ certificate, onClose }: { certificate: LearnerCertificateDto; onClose: () => void }) {
    const api = useApi();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef(false);
    const active = useRef(true);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

    const submit = async () => {
        if (!api || pending.current || !name.trim() || !email.trim() || !address.trim()) return;
        pending.current = true; setBusy(true); setError('');
        try {
            await api.requestCertificatePrint(certificate.key, { recipient_name: name.trim(), recipient_email: email.trim(), postal_address: address.trim() });
            if (active.current) { setName(''); setEmail(''); setAddress(''); setSent(true); }
        } catch (failure: any) {
            if (active.current) setError(failure?.response?.data?.message || 'Your request could not be sent. Please try again later.');
        } finally {
            pending.current = false;
            if (active.current) setBusy(false);
        }
    };

    if (sent) return <GeneralInfoDialog visible title="Request sent" message="Your printed copy request has been sent to the site administrator. Your copy of the certificate will be sent to the address supplied. The administrator will get in touch with you at the recipient email address if there are any questions." onClose={onClose} />;

    return <Dialog visible modal header="Order printed copy" style={{ width: '36rem', maxWidth: '95vw' }}
        draggable={false} closable={!busy} closeOnEscape={!busy} onHide={() => { if (!pending.current) onClose(); }}>
        <form className="flex flex-column gap-3" onSubmit={event => { event.preventDefault(); void submit(); }}>
            <p>Request a printed copy of your certificate for <strong>{certificate.course_name}</strong>. Your copy of the certificate will be sent to the address supplied. The administrator will get in touch with you at the recipient email address if there are any questions.</p>
            <label htmlFor="certificate-delivery-name">Recipient name</label>
            <InputText id="certificate-delivery-name" value={name} required maxLength={200} disabled={busy}
                autoComplete="shipping name" className="w-full" onChange={event => setName(event.target.value)} />
            <label htmlFor="certificate-delivery-email">Recipient email</label>
            <InputText id="certificate-delivery-email" type="email" value={email} required maxLength={254} disabled={busy}
                autoComplete="shipping email" className="w-full" onChange={event => setEmail(event.target.value)} />
            <label htmlFor="certificate-delivery-address">Postal address</label>
            <InputTextarea id="certificate-delivery-address" value={address} required maxLength={2000} rows={5} disabled={busy}
                autoComplete="shipping street-address" className="w-full" onChange={event => setAddress(event.target.value)} />
            <small>Include the street address, city, postcode and country. These details will be emailed to the administrator and will not be saved in the website database.</small>
            {error && <div role="alert" className="p-3 border-round border-1 border-red-400 text-red-400">{error}</div>}
            <div className="flex justify-content-end gap-2">
                <Button type="button" label="Cancel" outlined disabled={busy} onClick={onClose} />
                <Button type="submit" label="Send request" icon="pi pi-send" loading={busy} disabled={busy || !api || !name.trim() || !email.trim() || !address.trim()} />
            </div>
        </form>
    </Dialog>;
}
